import { useEffect, useMemo } from "react";

export interface SegmentAnalyticsAdapter {
  reset: () => Promise<unknown>;
}

let segmentInstance: SegmentAnalyticsAdapter | null = null;
let segmentInstanceWriteKey: string | null = null;
function loadSegmentInstance(
  writeKey: string,
  load: (writeKey: string) => SegmentAnalyticsAdapter,
): SegmentAnalyticsAdapter {
  if (!segmentInstance || segmentInstanceWriteKey !== writeKey) {
    segmentInstance = load(writeKey);
    segmentInstanceWriteKey = writeKey;
  }

  return segmentInstance;
}

async function resetSegmentIfPresent(): Promise<void> {
  await segmentInstance?.reset();
}

interface UseConsentGatedSegmentOptions {
  writeKey: string;
  performanceAllowed: boolean;
  load: (writeKey: string) => SegmentAnalyticsAdapter;
}

export const useConsentGatedSegment = <T extends SegmentAnalyticsAdapter>({
  writeKey,
  performanceAllowed,
  load,
}: UseConsentGatedSegmentOptions): T | null => {
  const analytics = useMemo(() => {
    if (!writeKey || !performanceAllowed) {
      return null;
    }

    return loadSegmentInstance(writeKey, load) as T;
  }, [load, performanceAllowed, writeKey]);

  useEffect(() => {
    if (performanceAllowed) return;

    void resetSegmentIfPresent().catch(() => {
      // ignore reset errors
    });
  }, [performanceAllowed]);

  return analytics;
};
